/**
 * useLocationSharing Hook
 *
 * App-wide orchestrator for the live location-sharing feature. Mount once.
 *
 * Responsibilities:
 * - Consumes the shared `useLocalLocationTracking` core for the always-on local
 *   watch (writes the local dot to the store, never prompts on mount).
 * - Broadcast gate: when `shareLocation` is on AND permission is granted,
 *   broadcasts each fix to the workspace (throttled). When sharing is turned
 *   off, tells the server once and keeps the local watch running.
 * - Keepalive re-broadcast so a stationary user does not expire server-side.
 * - Stops sharing when permission flips to `denied` (the core clears the local
 *   dot; this hook makes the single server-side "stopped" call).
 */
import { useCallback, useEffect, useRef } from 'react';

import { useGeoMarkupStore } from '@/store';
import {
  PERMISSION_DENIED,
  PERMISSION_GRANTED,
  USER_LOCATION_BROADCAST_THROTTLE_MS,
  USER_LOCATION_TTL_MS,
} from '@/constants';
import type { MemberLocation } from '@/types';

import { useLocalLocationTracking } from './useLocalLocationTracking';

/**
 * Keepalive re-broadcast interval. A stationary user stops producing
 * `watchPosition` fixes, so we re-send the last known position at half the
 * backend TTL to guarantee at least one refresh before the entry expires.
 */
const KEEPALIVE_INTERVAL_MS = USER_LOCATION_TTL_MS / 2;

/** Imperative senders supplied by `usePresence`, passed through to broadcasts. */
interface UseLocationSharingParams {
  sendLocation: (lat: number, lng: number, heading?: number) => void;
  stopSharingLocation: () => void;
}

/**
 * Orchestrates the broadcast gate, keepalive, and permission-revoked teardown
 * on top of the shared local-watch core for live location sharing.
 *
 * @example
 * const { sendLocation, stopSharingLocation } = usePresence({ ... });
 * useLocationSharing({ sendLocation, stopSharingLocation });
 */
export function useLocationSharing(params: UseLocationSharingParams): void {
  const { sendLocation, stopSharingLocation } = params;
  const shareLocation = useGeoMarkupStore((state) => state.map.shareLocation);
  const locationPermission = useGeoMarkupStore(
    (state) => state.map.locationPermission
  );

  // `shareLocation` changes over the hook's life, but the long-lived
  // broadcast/keepalive callbacks must read its LATEST value without being torn
  // down — so it is mirrored into a ref. The `sendLocation`/`stopSharingLocation`
  // senders are stable `useCallback` identities from `usePresence`, so they are
  // used directly with no ref mirror.
  const shareLocationRef = useRef(shareLocation);

  const lastBroadcastAtRef = useRef<number>(0);
  const keepaliveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Per-fix broadcast gate. Invoked by the shared core AFTER the local dot is
  // written: send each fix only while sharing is on and the throttle allows.
  // Memoized on the stable `sendLocation` so the `{ onFix }` options object is
  // not reallocated every render (the core stores `onFix` in a ref).
  const maybeBroadcast = useCallback(
    (location: MemberLocation): void => {
      if (!shareLocationRef.current) {
        return;
      }

      const now = Date.now();
      if (
        now - lastBroadcastAtRef.current >=
        USER_LOCATION_BROADCAST_THROTTLE_MS
      ) {
        lastBroadcastAtRef.current = now;
        sendLocation(location.lat, location.lng, location.heading);
      }
    },
    [sendLocation]
  );

  // Always-on local watch comes from the shared core. The core owns the watch
  // lifecycle, the local dot, and the permission store value. (Effect 0 here.)
  useLocalLocationTracking({ onFix: maybeBroadcast });

  // Keep the share-state ref current, emit the single "stopped sharing" signal
  // when sharing is switched off, and run the keepalive re-broadcast while
  // sharing is active with permission granted. The senders are stable, so only
  // the share/permission inputs drive this effect. (Effect 1/3)
  useEffect(() => {
    const wasSharing = shareLocationRef.current;
    shareLocationRef.current = shareLocation;

    if (wasSharing && !shareLocation) {
      // Reset the throttle so re-enabling broadcasts immediately.
      lastBroadcastAtRef.current = 0;
      stopSharingLocation();
    }

    function clearKeepalive(): void {
      if (keepaliveTimerRef.current !== null) {
        clearInterval(keepaliveTimerRef.current);
        keepaliveTimerRef.current = null;
      }
    }

    // Re-send the last known position periodically so a stationary user (who
    // stops producing watch fixes) does not expire from the server-side hash.
    // The last position is the local dot already in the store (the core writes
    // it on every fix); on `denied` the store clears it to null, so the
    // keepalive naturally has nothing to send.
    const canKeepalive =
      shareLocation && locationPermission === PERMISSION_GRANTED;

    if (canKeepalive) {
      // The timer is already null here (prior cleanup / else path cleared it).
      keepaliveTimerRef.current = setInterval(() => {
        const lastPosition = useGeoMarkupStore.getState().map.liveLocation;
        if (!shareLocationRef.current || lastPosition === null) {
          return;
        }
        lastBroadcastAtRef.current = Date.now();
        sendLocation(lastPosition.lat, lastPosition.lng, lastPosition.heading);
      }, KEEPALIVE_INTERVAL_MS);
    } else {
      clearKeepalive();
    }

    return clearKeepalive;
  }, [shareLocation, locationPermission, sendLocation, stopSharingLocation]);

  // Stop sharing when permission flips to `denied`. The shared core already
  // stopped the watch and cleared the local dot; this hook only owns the
  // single server-side "stopped sharing" call. (Effect 2/3)
  useEffect(() => {
    if (locationPermission === PERMISSION_DENIED && shareLocationRef.current) {
      stopSharingLocation();
    }
  }, [locationPermission, stopSharingLocation]);
}
